import * as React from "react";
import { Modal } from "antd";
import { observer } from "mobx-react-lite";
import { BootstrapConfig } from "../../schema/types/agent";
import { CodeEditor } from "../group/identityMatcher/codeEditor";
import { ConsoleContext } from "../../mobxStores/consoleContext/consoleContext";
import { AgentScriptTabs } from "./agentScriptTabs";
import { NGButton } from "../../newcomponents/button/Button";
import { Upload } from "react-feather";

interface Props {
  agentName: string;
  visible: boolean;
  onClose: () => void;
}

const DownloadScriptsModalRaw: React.FC<Props> = ({ agentName, visible, onClose }) => {
  const [step, setStep] = React.useState(1);
  const [bootstrapConfigText, setBootstrapConfigText] = React.useState("");
  const [bootstrapConfig, setBootstrapConfig] = React.useState<BootstrapConfig | null>(null);
  const [configError, setConfigError] = React.useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  function validateAndParseConfig(text: string): { valid: boolean; config: BootstrapConfig | null; error: string } {
    if (!text.trim()) {
      return { valid: false, config: null, error: "Bootstrap config is required" };
    }

    try {
      const parsed = JSON.parse(text);

      if (!parsed.registrationToken || !parsed.agentId || !parsed.agentLink || !parsed.hubEndpoint) {
        return {
          valid: false,
          config: null,
          error:
            "Invalid bootstrap config: missing required fields (registrationToken, agentId, agentLink, hubEndpoint)",
        };
      }

      return { valid: true, config: parsed as BootstrapConfig, error: "" };
    } catch {
      return { valid: false, config: null, error: "Invalid JSON format" };
    }
  }

  function handleConfigChange(text: string) {
    setBootstrapConfigText(text);
    const { valid, config, error } = validateAndParseConfig(text);
    setConfigError(error);
    setBootstrapConfig(valid ? config : null);
  }

  function handleFileImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      handleConfigChange(content);
    };
    reader.readAsText(file);
    // Reset so the same file can be re-imported
    e.target.value = "";
  }

  // Reset state when modal closes
  React.useEffect(() => {
    if (!visible) {
      setStep(1);
      setBootstrapConfigText("");
      setBootstrapConfig(null);
      setConfigError("");
    }
  }, [visible]);

  return (
    <Modal
      title={step === 1 ? "Provide Bootstrap Config" : "Download Scripts"}
      open={visible}
      maskClosable={false}
      onCancel={onClose}
      destroyOnClose
      styles={{
        body: { height: "70vh", display: "flex", flexDirection: "column" },
        content: { minWidth: 600, maxWidth: "90vw" },
      }}
      width={1200}
      footer={
        <div className="flex items-center justify-between">
          <div>{step === 2 && <NGButton variant="secondary" onClick={() => setStep(1)}>Back</NGButton>}</div>
          <div className="flex items-center gap-4">
            {step === 1 ? (
              <>
                <NGButton variant="secondary" onClick={onClose}>Cancel</NGButton>
                <NGButton variant="primary" onClick={() => setStep(2)} disabled={!bootstrapConfig}>
                  Next
                </NGButton>
              </>
            ) : (
              <NGButton variant="primary" onClick={onClose}>
                Done
              </NGButton>
            )}
          </div>
        </div>
      }
    >
      {step === 1 ? (
        <div className="p-4">
          <div className="mb-4">
            <div className="mb-2">Paste or import your agent bootstrap config JSON:</div>
            <NGButton
              variant="secondary"
              size="small"
              iconPosition="left"
              renderIcon={(hover, props) => <Upload {...props} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Import JSON
            </NGButton>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: "none" }}
              onChange={handleFileImport}
            />
          </div>
          <CodeEditor
            value={bootstrapConfigText}
            setValue={handleConfigChange}
            language="json"
            options={{
              basicSetup: { lineNumbers: true },
              style: { maxHeight: "calc(70vh - 180px)", overflow: "auto" },
            }}
            minHeight="200px"
          />
          {configError && bootstrapConfigText.trim() && (
            <div className="color-danger text-sm mt-2">{configError}</div>
          )}
        </div>
      ) : (
        <div className="h-full p-4">
          <AgentScriptTabs org={ConsoleContext.org!} agentName={agentName} bootstrapConfig={bootstrapConfig!} />
        </div>
      )}
    </Modal>
  );
};

export const DownloadScriptsModal = observer(DownloadScriptsModalRaw);
